import * as React from 'react';

import styled, {CSS} from '@pinecast/styles';

export type Sizes = 'normal' | 'large';

export const InputWrapper = styled(
  'div',
  (
    {
      $disabled,
      $prefixCount = 0,
      $suffixCount = 0,
      $invalid,
      $size = 'normal',
    }: {
      $disabled: boolean;
      $prefixCount?: number;
      $suffixCount?: number;
      $invalid: boolean;
      $size?: Sizes;
    },
    designColors,
  ) => ({
    alignItems: 'center',
    backgroundColor: designColors.contentBackground,
    borderRadius: 4,
    display: 'grid',
    gridTemplateColumns: `${'max-content '.repeat(
      $prefixCount,
    )}1fr ${'max-content '.repeat($suffixCount)}`,
    height: $size === 'normal' ? 32 : 40,
    boxShadow: $invalid
      ? `0 0 0 1px ${designColors.borderRedAccent}, 0 0 0 0 ${designColors.borderRedLight}`
      : `0 0 0 1px ${designColors.inputBorder}, 0 0 0 0 ${designColors.accentBlueLight}`,
    lineHeight: $size === 'normal' ? '32px' : '40px',
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    opacity: $disabled ? 0.5 : 1,
    transition: 'box-shadow 0.2s, opacity 0.2s',

    ':focus-within': {
      boxShadow: $invalid
        ? `0 0 0 1px ${designColors.borderRedAccent}, 0 0 0 3px ${designColors.borderRedLight}`
        : `0 0 0 1px ${designColors.accentBlue}, 0 0 0 3px ${designColors.accentBlueLight}`,
    },
    ':focus-within .TextInput--Cap': {
      boxShadow: $invalid
        ? `0 0 0 1px ${designColors.borderRedAccent}`
        : `0 0 0 1px ${designColors.accentBlue}`,
    },
  }),
);

const Prefix = styled(
  'span',
  (
    {
      $invalid,
      $size,
      $columnId,
    }: {$invalid: boolean; $size: Sizes; $columnId: string},
    designColors,
  ) => ({
    alignItems: 'center',
    backgroundColor: 'transparent',
    borderBottomLeftRadius: 4,
    borderTopLeftRadius: 4,
    boxShadow: $invalid
      ? `0 0 0 1px ${designColors.borderRedAccent}`
      : `0 0 0 1px ${designColors.inputBorder}`,
    color: designColors.contentTextAccent,
    display: 'flex',
    flex: '0 0',
    fontSize: $size === 'normal' ? 14 : 20,
    gridColumn: $columnId,
    gridRow: '1 / 1',
    height: '100%',
    marginRight: 1,
    padding: '0 8px',
    transition: 'box-shadow 0.2s',
    ':not(:empty) + input': {
      marginLeft: 0,
    },
    ':not(:empty) + .TextInputWrapper--Prefix': {
      borderBottomLeftRadius: 0,
      borderTopLeftRadius: 0,
    },
  }),
);
const Suffix = styled(
  'span',
  (
    {
      $columnId,
      $invalid,
      $size,
    }: {$columnId: string; $invalid: boolean; $size: Sizes},
    designColors,
  ) => ({
    alignItems: 'center',
    backgroundColor: 'transparent',
    boxShadow: $invalid
      ? `0 0 0 1px ${designColors.borderRedAccent}`
      : `0 0 0 1px ${designColors.inputBorder}`,
    color: designColors.contentTextAccent,
    display: 'flex',
    flex: '0 0',
    fontSize: $size === 'normal' ? 14 : 20,
    gridColumn: $columnId,
    gridRow: '1 / 1',
    height: '100%',
    marginLeft: 1,
    padding: '0 8px',
    transition: 'box-shadow 0.2s',
    ':last-of-type': {
      borderBottomRightRadius: '4px',
      borderTopRightRadius: '4px',
    },
  }),
);

export interface Props {
  children: React.ReactNode;
  className?: string;
  disabled: boolean;
  endCapStyle?: CSS;
  invalid: boolean;
  prefix?: React.ReactNode;
  size: Sizes;
  style?: CSS;
  suffix?: React.ReactNode;
}

export function arrayify(
  node: React.ReactNode | Array<React.ReactNode>,
): Array<React.ReactNode> {
  if (!node) {
    return [];
  }
  if (Array.isArray(node)) {
    return node.filter(x => x);
  }
  return [node];
}

const TextInputWrapper = ({
  children,
  className,
  disabled,
  endCapStyle,
  invalid,
  prefix,
  size = 'normal',
  style,
  suffix,
}: Props) => {
  const prefixes = arrayify(prefix);
  const suffixes = arrayify(suffix);
  return (
    <InputWrapper
      $disabled={disabled}
      $prefixCount={prefixes.length}
      $suffixCount={suffixes.length}
      $invalid={invalid}
      $size={size}
      className={className}
      style={style}
    >
      {prefix &&
        prefixes.map((prefix, i) => (
          <Prefix
            key={i}
            $columnId={`${i + 1} / ${i + 1}`}
            $invalid={invalid || false}
            $size={size || 'normal'}
            className="TextInput--Cap TextInputWrapper--Prefix"
            style={endCapStyle}
          >
            {prefix}
          </Prefix>
        ))}
      {children}
      {suffix &&
        suffixes.map((suffix, i) => (
          <Suffix
            key={i}
            $columnId={`${i + 2 + prefixes.length} / ${
              i + 2 + prefixes.length
            }`}
            $invalid={invalid || false}
            $size={size || 'normal'}
            className="TextInput--Cap TextInputWrapper--Suffix"
            style={endCapStyle}
          >
            {suffix}
          </Suffix>
        ))}
    </InputWrapper>
  );
};

export default TextInputWrapper;
